<template>
  <div>
    <base-header class="pb-6 pb-8 pt-8 pt-md-5">
    </base-header>

    <div class="container-fluid mt--7 request-create">
      <div class="row align-items-start">
          <div class="col-12 col-xl-8">
            <card shadow>
              <template #header>
                <div class="care-panel-heading">
                  <span class="care-panel-heading__icon" aria-hidden="true"><i class="far fa-calendar-plus"></i></span>
                  <div>
                    <h5 class="mb-1">{{ $t(isUpdate ? "Edit" : "New Request") }}</h5>
                    <p>{{ $t("Plan your shift, set a deadline and review the cost.") }}</p>
                  </div>
                </div>
              </template>
              <p v-if="pageLoading" role="status">{{ $t("Loading...") }}</p>
              <div v-else-if="pageError" class="alert alert-danger" role="alert">{{ pageError }}</div>
              <form v-if="!pageLoading && !pageError" @submit.prevent="submit">
                <p v-if="!canCreateRequests" class="alert alert-warning" role="alert">{{ $t("You are not allowed to create or edit requests. Please contact your administrator.") }}</p>
                <fieldset :disabled="!canCreateRequests || loadingProcess === 'processing'">
                <div class="request-create__fields">
                  <h6 class="care-section-title"><span>01</span>{{ $t("Shift details") }}</h6>
                  <div class="row">
                    <div v-if="userRole === 'SYSTEM_USER'" class="col-lg-6">
                      <div class="form-group has-label" :class="{'has-danger': v$.model.clientId.$error }">
                        <label class="form-label">{{ $t("Select Client") }}</label>
                        <multiselect
                            v-model="model.clientId"
                            :disabled="isUpdate"
                            :show-labels="false"
                            label="name"
                            track-by="id"
                            :placeholder="$t('Select Client')"
                            :options="clients" :multiple="false"
                            :error="v$.model.clientId.$errors.map( err => { return err.$message })[0]"
></multiselect>
                        <div v-if="v$.model.clientId.$invalid" class="text-danger invalid-feedback" style="display: block;">
                          {{ v$.model.clientId.$errors.map( err => { return err.$message })[0] }}
                        </div>
                      </div>
                    </div>
                    <div class="col-lg-6">
                      <div class="form-group has-label" :class="{'has-danger': v$.model.position.$error }">
                        <label class="form-label">{{ $t("Job position") }}</label>
                        <multiselect
                            v-model="model.position"
                            :show-labels="false"
                            label="name"
                            track-by="id"
                            :placeholder="$t('Select positions')"
                            :options="availablePositions" :multiple="false"
                            :error="v$.model.position.$errors.map( err => { return err.$message })[0]"
                        ></multiselect>
                        <div v-if="v$.model.position.$invalid" class="text-danger invalid-feedback" style="display: block;">
                          {{ v$.model.position.$errors.map( err => { return err.$message })[0] }}
                        </div>
                      </div>
                    </div>
</div>
                  <h6 class="care-section-title"><span>02</span>{{ $t("Schedule & deadline") }}</h6>
                  <div class="row">
                    <div class="col-lg-6">
                      <div class="form-group has-label" :class="{'has-danger': v$.model.startDate.$error }">
                        <label class="form-label">{{ $t("Shift start") }}</label>
                      <VueDatePicker
                          v-model="model.startDate"
                          :start-time="{ hours: 10, minutes: 0 }"
                          minutes-increment="10"
                          minutes-grid-increment="10"
                          format="dd/MM/yyyy HH:mm"
                          :flow="['calendar', 'time']"
                          :placeholder="$t('Shift start')"
                          :min-date="userRole === 'SYSTEM_USER' ? null : $moment().startOf('day').toDate()"
                      ></VueDatePicker>
                        <div v-if="v$.model.startDate.$invalid" class="text-danger invalid-feedback" style="display: block;">
                          {{ v$.model.startDate.$errors.map( err => { return err.$message })[0] }}
                        </div>
                      </div>
                    </div>
                    <div class="col-lg-6">
                      <div class="form-group has-label" :class="{'has-danger': v$.model.endDate.$error }">
                        <label class="form-label">{{ $t("Shift end") }}</label>
                      <VueDatePicker
                          v-model="model.endDate"
                          :start-time="{ hours: 10, minutes: 0 }"
                          minutes-increment="10"
                          minutes-grid-increment="10"
                          format="dd/MM/yyyy HH:mm"
                          :flow="['calendar', 'time']"
                          :placeholder="$t('Shift end')"
                          :min-date="model.startDate ? $moment(model.startDate).startOf('minute').add(1, 'minute').toDate() : null"
                      ></VueDatePicker>
                        <div v-if="v$.model.endDate.$invalid" class="text-danger invalid-feedback" style="display: block;">
                          {{ v$.model.endDate.$errors.map( err => { return err.$message })[0] }}
                        </div>
                        </div>
                    </div>
                    <div v-if="shiftDurationMinutes !== null" class="col-12">
                      <div class="care-insight" role="status" aria-live="polite">
                        <i class="far fa-clock" aria-hidden="true"></i>
                        <div><span>{{ $t("Shift duration") }}</span><strong>{{ formatDuration(shiftDurationMinutes) }}</strong></div>
                        <small v-if="spansDates">{{ $t("Ends on {date}", {date: $moment(model.endDate).format("DD/MM/YYYY")}) }}</small>
                      </div>
                    </div>
                    <div v-if="isUpdate || model.startDate > new Date()" class="col-lg-6">
                      <div class="form-group has-label" :class="{'has-danger': v$.model.deadline.$error }">
                        <label class="form-label">{{ $t("Claim deadline") }}*</label>
                        <multiselect
                            v-model="selectedDeadline"
                            :options="deadlineOptions"
                            label="label"
                            track-by="key"
                            :show-labels="false"
                            :allow-empty="false"
                            :placeholder="$t('Select option')"
                        />

                        <div v-if="v$.model.deadline.$invalid" class="text-danger invalid-feedback" style="display: block;">
                          {{ v$.model.deadline.$errors.map( err => { return err.$message })[0] }}
                        </div>
                      </div>
                    </div>
                    <div v-if="deadlineLeadMinutes !== null && model.startDate > new Date()" class="col-lg-6 request-create__deadline-summary">
                      <div class="care-insight" role="status" aria-live="polite">
                        <i class="far fa-calendar-check" aria-hidden="true"></i>
                        <div><span>{{ $t("Claims close") }}</span><strong>{{ deadlineLeadMinutes === 0 ? $t("At shift start") : $t("{duration} before shift start", {duration: formatDuration(deadlineLeadMinutes)}) }}</strong><small>{{ $moment(model.deadline).format("DD/MM/YYYY HH:mm") }}</small></div>
                      </div>
                    </div>
                    <div v-if="model.startDate > new Date()" class="col-md-12">
                      <div class="text-start pt-0 pb-4 text-black-50">
                        <label><b>* {{ $t("Claim deadline") }}</b> {{ $t("is the latest time by which a request can be claimed.") }}</label>
                      </div>
                    </div>
                  <div v-if="!isUpdate && userRole === 'SYSTEM_USER'" class="col-md-12">
                    <div class="form-group has-label" :class="{'has-danger': v$.model.claimedBy.$error }">
                      <label class="form-label">{{ $t("Partner to assign shift") }}</label>
                      <multiselect
                          v-model="model.claimedBy"
                          :show-labels="false"
                          label="name"
                          track-by="id"
                          :internal-search="false"
                          :searchable="true"
                          :loading="partnerSearchLoading"
                          :placeholder="$t('Search fo partner')"
                          :options="employees" :multiple="false"
                          @search-change="asyncFindPartners"
                      >
                        <template #noResult>
                          <span>No partners found. Remember you are searching by name with 3 characters minimum.</span>
                        </template>
                        <template #noOptions>
                          <span>Type the name to search</span>
                        </template>
                      </multiselect>
                      <div v-if="v$.model.claimedBy.$invalid" class="text-danger invalid-feedback" style="display: block;">
                        {{ v$.model.claimedBy.$errors.map( err => { return err.$message })[0] }}
                      </div>
                    </div>
                  </div>
                  <div v-if="userRole === 'SYSTEM_USER' && !model.claimedBy" class="col-md-12">
                    <div class="form-check form-switch text-start mb-4">
                      <input id="lastMinuteBoostEnabled" v-model="model.lastMinuteBoostEnabled" class="form-check-input" type="checkbox" role="switch">
                      <label class="form-check-label" for="lastMinuteBoostEnabled">{{ $t("Last Minute Boost") }}</label>
                      <small class="d-block text-muted">{{ $t("When off, this shift stays at full price inside the last-minute window.") }}</small>
                    </div>
                  </div>
                  </div>
                </div>

                <section v-if="requestCost" class="care-summary" :aria-label="$t('Request Cost')">
                  <div class="care-summary__total">
                    <div><span class="care-summary__label">{{ $t("Total cost") }}</span><p>{{ $t("For the selected shift") }}</p></div>
                    <strong>{{ formatCost(requestCost.totalCost) }}</strong>
                  </div>
                  <div v-if="userRole !== 'CLIENT'" class="care-summary__breakdown">
                    <div>
                      <span>{{ $t("Employee earnings") }}</span>
                      <div v-if="boostComparison" class="care-price-comparison">
                        <del v-if="requestCost.employeeEarnings !== boostComparison.boosted.employeeEarnings" class="care-price-comparison__previous"><span class="visually-hidden">{{ $t("Standard price") }}: </span>{{ formatCost(requestCost.employeeEarnings) }}</del>
                        <strong><span class="visually-hidden">{{ $t("Boost price") }}: </span>{{ formatCost(boostComparison.boosted.employeeEarnings) }}</strong>
                      </div>
                      <strong v-else>{{ formatCost(requestCost.employeeEarnings) }}</strong>
                    </div>
                    <div>
                      <span>{{ $t("Company commission") }}</span>
                      <div v-if="boostComparison" class="care-price-comparison">
                        <del v-if="requestCost.companyCommission !== boostComparison.boosted.companyCommission" class="care-price-comparison__previous"><span class="visually-hidden">{{ $t("Standard price") }}: </span>{{ formatCost(requestCost.companyCommission) }}</del>
                        <strong><span class="visually-hidden">{{ $t("Boost price") }}: </span>{{ formatCost(boostComparison.boosted.companyCommission) }}</strong>
                      </div>
                      <strong v-else>{{ formatCost(requestCost.companyCommission) }}</strong>
                    </div>
                  </div>
                  <p v-if="boostComparison" class="care-summary__note" role="status">{{ $t("Automatic Boost preview. These prices will apply when the scheduler activates Boost; the organisation total stays the same.") }}</p>
                  <p v-if="userRole === 'CLIENT'" class="care-summary__note">{{ $t("Total need to be paid directly to the Person who will fulfill this shift.") }}</p>
                </section>
                <div class="care-form-actions">
                  <base-button v-if="!isUpdate" class="me-2" outline type="info" @click="clearForm">{{ $t("Reset") }}</base-button>
                  <submit-button :alert-message="errorMessage" :state="loadingProcess" @clicked="submit">{{ $t(isUpdate ? 'Update' : 'Submit') }}</submit-button>
                </div>
                </fieldset>
              </form>
            </card>
          </div>
          <aside v-if="model.lastMinuteBoostEnabled !== false" class="col-12 col-xl-4 mt-4 mt-xl-0" :aria-label="$t('Last Minute Boost')">
            <last-minute-boost-preview
                :deadline-hours="model.startDate && model.deadline ? $moment(model.startDate).diff($moment(model.deadline), 'hours', true) : null"
                :show-choices="false"
            />
          </aside>
      </div>
    </div>
  </div>
</template>
<script>
import {canCreateRequest, canEditRequest, requestErrorMessage, getRequestDeadlineOptions} from "@/helpers/requestHelper"
import FirebaseFunctionsHelper from "@/helpers/firebaseFunctionsHelper"
import useVuelidate from "@vuelidate/core"
import {helpers, required, requiredIf} from "@vuelidate/validators"
import LastMinuteBoostPreview from "@/components/LastMinuteBoostPreview.vue"
import {getBoostPricingComparison} from "@/helpers/boostPricing"
import {calculateShiftCost} from "@/helpers/shiftCostCalculator"

export default {
  name: "newRequest",
  components: {LastMinuteBoostPreview},
  setup: () => ({ v$: useVuelidate() }),
  data() {
    return {
      pricingPreviewNow: Date.now(),
      pricingPreviewTimer: null,
      partnerSearchLoading: false,
      clientConfigs: null,
      refreshKey: 1,
      positions: [],
      clients: [],
      employees: [],
      isUpdate: false,
      pageLoading: true,
      pageError: "",
      originalRequest: null,
      deadlineOffsetDays: 1 / 24,
      clientPositionIds: [],
      errorMessage: "",
      loadingProcess: "",
      loadingMessage: "",
      model: {
        clientId: null,
        claimedBy: null,
        lastMinuteBoostEnabled: true,
        deadline: null,
        startDate: null,
        endDate: null,
        position: null,
        cost: {
          totalCost: null,
          employeeEarnings: null,
          companyCommission: null,
        }
      }

    }
  },
  validations () {
    return {
      model: {
        clientId: {
          required: helpers.withMessage(this.$t("Value is required"), required)
        },
        deadline: {
          required: helpers.withMessage(this.$t("Value is required"), required),
          maxValue: helpers.withMessage(this.$t("Deadline must be at or before shift start."), value => !value || !this.model.startDate || value <= this.model.startDate),
          future: helpers.withMessage(this.$t("Choose a future deadline."), value => this.userRole !== "CLIENT" || !value || value > new Date())
        },
        startDate: {
          required: helpers.withMessage(this.$t("Value is required"), required),
         future: helpers.withMessage(this.$t("Choose a future start time."), value => this.userRole !== "CLIENT" || !value || value > new Date()),
         // maxIfNotEmpty: helpers.withMessage("The maximum value allowed is "+ this.$moment(this.model.endDate).format(), maxIfNotEmpty(this.model.endDate))
        },
        endDate: {
          $autoDirty: true,
          required: helpers.withMessage(this.$t("Value is required"), required),
          minValue: helpers.withMessage(this.$t("Shift end must be after shift start."), value => !value || !this.model.startDate || this.$moment(value).isAfter(this.model.startDate, "minute")),
          duration: helpers.withMessage(this.$t("Shift duration cannot exceed 31 days."), value => !this.isUpdate || !value || !this.model.startDate || value - this.model.startDate <= 31 * 86400000),
        },
        position: {
          required: helpers.withMessage(this.$t("Value is required"), required)
        },
        claimedBy: {
          requiredIf: helpers.withMessage(this.$t("Value is required"), requiredIf(!this.isUpdate && this.model.startDate < new Date()))
        }
      },
    }
  },
  computed: {
    deadlineOptions() {
      const options = getRequestDeadlineOptions(this.$t.bind(this))
      if (!options.some(option => option.key === this.deadlineOffsetDays)) {
        options.push({key: this.deadlineOffsetDays, label: this.$t("{duration} before shift start", {duration: this.formatDuration(this.deadlineOffsetDays * 1440)})})
      }
      return options
    },
    selectedDeadline: {
      get() {
        return this.deadlineOptions.find(option => option.key === this.deadlineOffsetDays)
      },
      set(option) {
        if (!option || option.$isDisabled) return
        this.deadlineOffsetDays = option.key
        this.updateDeadline()
      }
    },
    canCreateRequests() {
      return canCreateRequest(this.$store.state.user.claims, this.$store.state.user.data)
    },
    availablePositions() {
      return this.isUpdate ? this.positions.filter(position => this.clientPositionIds.includes(position.id)) : this.positions
    },
    userRole(){
      return this.$store.state.user.claims.role ?? null
    },
    shiftDurationMinutes() {
      if (!this.model.startDate || !this.model.endDate) return null
      if (!this.$moment(this.model.endDate).isAfter(this.model.startDate, "minute")) return null
      const minutes = this.$moment(this.model.endDate).diff(this.$moment(this.model.startDate), "minutes", true)
      return Number.isFinite(minutes) && minutes > 0 ? Math.round(minutes) : null
    },
    deadlineLeadMinutes() {
      if (!this.model.startDate || !this.model.deadline) return null
      const minutes = this.$moment(this.model.startDate).diff(this.$moment(this.model.deadline), "minutes", true)
      return Number.isFinite(minutes) && minutes >= 0 ? Math.round(minutes) : null
    },
    spansDates() {
      return this.shiftDurationMinutes !== null && !this.$moment(this.model.startDate).isSame(this.model.endDate, "day")
    },
    boostComparison() {
      if (this.userRole !== "SYSTEM_USER" || this.isUpdate || !this.model.startDate || !this.model.deadline || this.deadlineLeadMinutes === null) return null
      return getBoostPricingComparison({
        status: "VERIFIED",
        claimedBy: this.model.claimedBy,
        lastMinuteBoostEnabled: this.model.lastMinuteBoostEnabled,
        cost: this.requestCost,
        mStartDate: this.$moment(this.model.startDate),
        mDeadline: this.$moment(this.model.deadline)
      }, this.requestCost, this.pricingPreviewNow)
    },
    requestCost() {
      if (!this.model.clientId || !this.model.position || !this.clientConfigs || this.shiftDurationMinutes === null || (this.isUpdate && this.shiftDurationMinutes > 31 * 1440)) return null
      const cost = calculateShiftCost(this.model.position, this.clientConfigs, this.model.startDate, this.model.endDate)
      return [cost.totalCost, cost.employeeEarnings, cost.companyCommission].every(value => Number.isFinite(value) && value >= 0) ? cost : null
    }
  },
  watch: {
    "model.startDate": function (newValue){
      if (!newValue || !this.$moment(newValue).isValid()) return

      if (this.model.endDate == null){
        this.model.endDate = this.$moment(newValue).add("8", "hours").toDate()
      }
      if (this.isUpdate && this.pageLoading) return
      if (!this.isUpdate && this.model.deadline == null && this.userRole === "CLIENT" &&
          newValue - new Date() <= this.deadlineOffsetDays * 86400000) {
        this.deadlineOffsetDays = 10 / 1440
      }
      this.updateDeadline()
    },
    "model.clientId": async function (client) {
      console.log("model.clientId", client)
      if (client && !this.pageLoading) {
        try {
          await this.getSettings(client.id)
        } catch (error) {
          this.clientConfigs = null
          this.errorMessage = requestErrorMessage(error, this.$t.bind(this))
          this.loadingProcess = "failed"
        }
      }
    },
  },
  beforeUnmount() {
    clearInterval(this.pricingPreviewTimer)
  },
  async mounted() {
    if (this.userRole === "SYSTEM_USER") this.pricingPreviewTimer = setInterval(() => { this.pricingPreviewNow = Date.now() }, 1000)
    this.isUpdate = Boolean(this.$route.params.id)
    try {
      await this.getPositions()
      if (this.isUpdate) {
        await this.getRequest()
      } else if (this.userRole === "SYSTEM_USER") {
        await this.getClients()
      } else {
        await this.getSettings(this.$store.state.user.claims.clientId)
      }
    } catch (error) {
      this.pageError = error.message || this.$t("Unable to load request.")
    } finally {
      this.pageLoading = false
    }
  },
  methods:{
    updateDeadline() {
      this.model.deadline = this.model.startDate ? new Date(this.model.startDate.getTime() - this.deadlineOffsetDays * 86400000) : null
    },
    formatDuration(totalMinutes) {
      const days = Math.floor(totalMinutes / 1440)
      const hours = Math.floor(totalMinutes % 1440 / 60)
      const minutes = totalMinutes % 60
      return [
        days ? this.$t("{count}d", {count: days}) : null,
        hours ? this.$t("{count}h", {count: hours}) : null,
        minutes ? this.$t("{count}min", {count: minutes}) : null
      ].filter(Boolean).join(" ")
    },
    formatCost(cents) {
      return new Intl.NumberFormat(this.$i18n.locale === "gr" ? "el-GR" : this.$i18n.locale, {style: "currency", currency: "EUR"}).format(cents / 100)
    },
    async asyncFindPartners(query){
      this.partnerSearchLoading = true
      await this.$store.dispatch("fetchEmployees", {limit: 50, name: query}).then((data) => {
        console.log("fetchEmployees", data)
        this.employees = data.data
        this.partnerSearchLoading = false
      })
    },
    async getEmployees(){
      if (this.employees.length === 0) {
        await this.$store.dispatch("fetchEmployees", {limit: 500}).then((data) => {
          console.log("fetchEmployees", data)
          this.employees = data.data
        })
      }
    },
    async getSettings(id) {
      const clientSettings = await this.$store.dispatch("fetchClient", id)
      this.clientPositionIds = clientSettings.positionIds ?? []
      if (this.isUpdate && this.userRole === "CLIENT" && clientSettings.status !== "VERIFIED") {
        throw new Error(this.$t("The organization is not verified"))
      }
      console.log("clientSettings id", id)
      console.log("clientSettings", clientSettings)
      if (!clientSettings.hasOwnProperty("configs") || clientSettings.configs == null) {
        this.clientConfigs = await this.$store.dispatch("fetchSettings")
      } else {
        this.clientConfigs = clientSettings.configs
      }
    },
    async getClients() {
      await this.$store.dispatch("fetchClients").then((data) => {
        // console.log("fetchClients", data)
        this.clients = data
      })
    },
    async getPositions() {
      this.positions = []
      let allPositions = await this.$store.dispatch("fetchAllPositions")
      if (this.userRole === "SYSTEM_USER") {
          this.positions = allPositions
      } else {
        await this.$store.dispatch("fetchClient", this.$store.state.user.claims.clientId).then((data) => {
          console.log("fetchClient fetchClient", data)
          if (data.hasOwnProperty("name")) {
            this.model.clientId = {id: this.$store.state.user.claims.clientId, name: data.name}
          }
          if (data.hasOwnProperty("positionIds")){
            data.positionIds.forEach( p => {

              let selected = allPositions.find(pos => pos.id === p)

              console.log("allPositions selected", selected)
              if (selected ?? false) {
                console.log("allPositions selected got it")
                this.positions.push(selected)
              }
            })
          }
        })
      }
    },
    customCityLabel (option) {
      return this.$t(option)
    },
    async getRequest() {
      const request = await this.$store.dispatch(this.userRole === "CLIENT" ? "fetchClientRequest" : "fetchRequest", this.$route.params.id)
      if (!canEditRequest(request, this.$store.state.user.claims, new Date(), this.$store.state.user.data)) {
        throw new Error(this.$t("This request can no longer be updated."))
      }
      await this.getSettings(request.clientId)
      const client = await this.$store.dispatch("fetchClient", request.clientId)
      this.deadlineOffsetDays = (request.mStartDate.valueOf() - request.mDeadline.valueOf()) / 86400000
      this.originalRequest = request
      this.clients = [{id: request.clientId, name: client.name}]
      this.model = {
        clientId: this.clients[0],
        position: this.availablePositions.find(position => position.id === request.position.id) ?? null,
        startDate: request.mStartDate.toDate(),
        endDate: request.mEndDate.toDate(),
        deadline: request.mDeadline.toDate(),
        claimedBy: null,
        lastMinuteBoostEnabled: request.lastMinuteBoostEnabled !== false
      }
      this.v$.$reset()
    },
    async submit() {
      let self = this
      if (!this.canCreateRequests) {
        this.loadingProcess = "failed"
        this.errorMessage = this.$t("You are not allowed to create or edit requests. Please contact your administrator.")
        return
      }
      if (this.pageLoading || this.pageError || this.loadingProcess === "processing") return
      if (this.isUpdate && !canEditRequest(this.originalRequest, this.$store.state.user.claims, new Date(), this.$store.state.user.data)) {
        this.errorMessage = this.$t("This request can no longer be updated.")
        this.loadingProcess = "failed"
        return
      }
      this.errorMessage = ""
      this.v$.$touch()

      if (this.v$.model.$invalid) {
        console.log(this.v$.$errors)
        return
      }

      // Recheck the clock on submit: an open form's previously valid deadline can expire.
      if (this.userRole === "CLIENT") {
        const now = new Date()
        const timingError = !(this.model.startDate > now) ? "Choose a future start time." :
          !(this.model.deadline > now) ? "Choose a future deadline." :
            !(this.model.deadline <= this.model.startDate) ? "Deadline must be at or before shift start." : null
        if (timingError) {
          this.errorMessage = this.$t(timingError)
          this.loadingProcess = "failed"
          return
        }
      }

      if (!this.requestCost) {
        this.errorMessage = this.$t("Cost cannot be calculated yet.")
        this.loadingProcess = "failed"
        return
      }

      let request = JSON.parse(JSON.stringify(this.model))
      request.cost = {...this.requestCost}

      request.positionId = request.position.id
      if (request.claimedBy && Object.hasOwn(request.claimedBy, "id") && this.userRole === "SYSTEM_USER") {
        request.claimedBy = request.claimedBy.id
      }

      delete request.position
      request.clientId = request.clientId.id
      // the backend ignores it from organizations, so only AllCare sends it
      if (this.userRole !== "SYSTEM_USER") delete request.lastMinuteBoostEnabled
      const payload = this.isUpdate ? {
        clientId: this.originalRequest.clientId,
        requestId: this.$route.params.id,
        startDate: request.startDate,
        endDate: request.endDate,
        deadline: request.deadline,
        positionId: request.positionId,
        ...(this.userRole === "SYSTEM_USER" && {lastMinuteBoostEnabled: request.lastMinuteBoostEnabled})
      } : {
        clientId: request.clientId,
        requests: [request]
      }

      // payload.requestStatus = payload.requestStatus.id

      console.log("payload", payload)

      this.loadingProcess = "processing"
      return FirebaseFunctionsHelper.callFunction(
          this.isUpdate ? "request-update" : "request-create",
          // eslint-disable-next-line no-undef
          payload
      )
          .then((result) => {
            console.log("res", result)
            // eslint-disable-next-line no-prototype-builtins
            if (result != null && typeof result.message === "string") {
              console.log("result message", result.message)
              self.loadingProcess = "failed"
              self.errorMessage = requestErrorMessage(result, this.$t.bind(this))
              return
            }
            console.log("submitUser result", result)
            this.loadingProcess = "done"
            this.$router.push({name: "Request", params: {id: this.isUpdate ? this.$route.params.id : (Array.isArray(result) ? result[0] : result)}})

          })
          .catch((error) => {
            self.loadingProcess = "failed"
            self.errorMessage = requestErrorMessage(error, this.$t.bind(this))
            console.log("submitUser error", error)
          })

      // setTimeout(() => {
      //   this.loadingProcess = "failed";
      //   this.loadingMessage = "There was an error.";
      //   //Add Tokens
      // }, 2000);
    },
    clearForm() {
      this.deadlineOffsetDays = 1 / 24
      this.loadingProcess = ""
      this.errorMessage = ""
      this.model = {
        clientId: this.userRole === "CLIENT" ? this.model.clientId : null,
        claimedBy: null,
        lastMinuteBoostEnabled: true,
        deadline: null,
        startDate: null,
        endDate: null,
        position: null,
        cost: {
          totalCost: null,
          employeeEarnings: null,
          companyCommission: null,
        }
      }
      this.v$.$reset()
    },
  },
}
</script>
<style scoped>
.request-create__deadline-summary { display: flex; align-items: center; }
.request-create__deadline-summary .care-insight { width: 100%; margin-top: 22px; }
@media (max-width: 575px) {
  .request-create__deadline-summary .care-insight { margin-top: 0; }
}
</style>
